import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';

import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import TagEditor from '../Widgets/TagEditor';

const generateId = () => Math.random().toString(36).slice(2, 11);

const createEntry = () => ({
  id: generateId(),
  path: '',
  rename: ''
});

const serializeEntries = (entries) =>
  entries
    .map(({ path, rename }) => ({ path: path || '', rename: rename || '' }))
    .filter(({ path }) => !!path);

const entriesEqual = (a = [], b = []) => JSON.stringify(serializeEntries(a)) === JSON.stringify(serializeEntries(b));

const ActionsCreateZipFile = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();

  const [targetPath, setTargetPath] = useState('');
  const [includeEventFile, setIncludeEventFile] = useState(true);
  const [flattenStructure, setFlattenStructure] = useState(false);
  const [allowMissingFiles, setAllowMissingFiles] = useState(false);
  const [fileEntries, setFileEntries] = useState([createEntry()]);

  const {
    isEnabled,
    setIsEnabled,
    conditions,
    isSaveDisabled,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleConditionsChange,
    handleCloseStatusWindow,
    updateSaveDisabledState
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'createzipfile',
    onSaveStatusChange,
    customComparison: (initial = {}) =>
      targetPath === (initial.target || '') &&
      includeEventFile === (initial.includeEventFile ?? true) &&
      flattenStructure === (initial.flattenStructure ?? false) &&
      allowMissingFiles === (initial.allowMissingFiles ?? false) &&
      entriesEqual(fileEntries, initial.files || []),
    customSaveData: () => ({
      target: targetPath,
      includeEventFile,
      flattenStructure,
      allowMissingFiles,
      files: serializeEntries(fileEntries)
    }),
    loadDataFunction: (data) => {
      const customData = data.customData || {};
      setTargetPath(customData.target || '');
      setIncludeEventFile(customData.includeEventFile !== undefined ? Boolean(customData.includeEventFile) : true);
      setFlattenStructure(customData.flattenStructure !== undefined ? Boolean(customData.flattenStructure) : false);
      setAllowMissingFiles(customData.allowMissingFiles !== undefined ? Boolean(customData.allowMissingFiles) : false);
      const files = Array.isArray(customData.files) && customData.files.length
        ? customData.files.map((entry) => ({
            id: generateId(),
            path: entry.path || '',
            rename: entry.rename || ''
          }))
        : [createEntry()];
      setFileEntries(files);
    }
  });

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  const { handleBrowseDirectory, FolderBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: false,
    ws: null,
    onFolderSelected: (folderPath) => {
      if (!folderPath) {
        return;
      }
      const clean = folderPath.replace(/[\\/]+$/, '');
      const separator = clean.includes('\\') ? '\\' : '/';
      const currentName = targetPath.split(/[/\\]/).pop() || 'bundle.zip';
      setTargetPath(`${clean}${separator}${currentName}`);
    }
  });

  useEffect(() => {
    updateSaveDisabledState();
  }, [targetPath, includeEventFile, flattenStructure, allowMissingFiles, fileEntries, updateSaveDisabledState]);

  const addEntry = () => setFileEntries((prev) => [...prev, createEntry()]);
  const removeEntry = (id) =>
    setFileEntries((prev) => (prev.length === 1 ? [createEntry()] : prev.filter((entry) => entry.id !== id)));

  const updateEntry = (id, field, value) =>
    setFileEntries((prev) => prev.map((entry) => (entry.id === id ? { ...entry, [field]: value } : entry)));

  return (
    <ActionSettingsLayout
      title={t('createzipfile', 'Create ZIP Archive')}
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
    >
      <Grid item xs={12}>
        <Card sx={{ mb: 3 }}>
          <CardHeader 
            title={t('workflow.create_zip.target_title', 'Archive Destination')} 
            titleTypographyProps={{ 
              sx: { fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 500 } 
            }}
            sx={{ p: { xs: 1.5, sm: 2 } }}
          />
          <CardContent>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                <Box sx={{ flexGrow: 1 }}>
                  <TagEditor
                    caseid={caseid}
                    value={targetPath}
                    onChange={setTargetPath}
                    placeholder="C:\\Exports\\{projectid}\\bundle.zip"
                    singleLine
                    tags={tags}
                  />
                </Box>
                <Button variant="outlined" onClick={handleBrowseDirectory}>
                  {t('browse', 'Browse')}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ mb: 3 }}>
          <CardHeader 
            title={t('workflow.create_zip.options_title', 'Options')} 
            titleTypographyProps={{ 
              sx: { fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 500 } 
            }}
            sx={{ p: { xs: 1.5, sm: 2 } }}
          />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={includeEventFile}
                      onChange={(event) => setIncludeEventFile(event.target.checked)}
                    />
                  }
                  label={t('workflow.create_zip.include_event_file', 'Include current event file')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={flattenStructure}
                      onChange={(event) => setFlattenStructure(event.target.checked)}
                    />
                  }
                  label={t('workflow.create_zip.flatten', 'Flatten folder structure')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={allowMissingFiles}
                      onChange={(event) => setAllowMissingFiles(event.target.checked)}
                    />
                  }
                  label={t('workflow.create_zip.allow_missing', 'Skip missing files')}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={t('workflow.create_zip.files_title', 'Files & Folders to include')}
            titleTypographyProps={{ 
              sx: { fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 500 } 
            }}
            sx={{ 
              p: { xs: 1.5, sm: 2 },
              '& .MuiCardHeader-action': { m: 0, alignSelf: 'center' } 
            }}
            action={
              <Button 
                startIcon={<AddIcon />} 
                variant="contained" 
                onClick={addEntry}
                size="small"
                sx={{ 
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  px: { xs: 1, sm: 2 }
                }}
              >
                {t('workflow.create_zip.add_file', 'Add File/Folder')}
              </Button>
            }
          />
          <CardContent>
            <Stack spacing={3}>
              {fileEntries.map((entry) => (
                <Card key={entry.id} variant="outlined">
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={11}>
                        <TagEditor
                    caseid={caseid}
                          placeholder={t('workflow.create_zip.path_label', 'Source path')}
                          value={entry.path}
                          onChange={(val) => updateEntry(entry.id, 'path', val)}
                          singleLine
                          tags={tags}
                        />
                      </Grid>
                      <Grid item xs={12} sm={1}>
                        <IconButton color="error" onClick={() => removeEntry(entry.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                      <Grid item xs={12}>
                        <TagEditor
                    caseid={caseid}
                          placeholder={t('workflow.create_zip.rename_label', 'Rename inside ZIP (optional)') + (flattenStructure ? ' (e.g. report.pdf)' : ' (e.g. reports/report.pdf)')}
                          value={entry.rename}
                          onChange={(val) => updateEntry(entry.id, 'rename', val)}
                          singleLine
                          tags={tags}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Tooltip
        title={t(
          'workflow.create_zip.files_hint',
          'Paths can reference files or folders. Folders will be added recursively.'
        )}
      >
        <Box component="span" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
          {t(
            'workflow.create_zip.files_hint',
            'Paths can reference files or folders. Folders will be added recursively.'
          )}
        </Box>
      </Tooltip>
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsCreateZipFile);
